import React, { useEffect, useRef, useState } from "react";
import { adminGetJson, adminPostJson } from "./adminApi";
import { AdminSpinner } from "./AdminLoadingUi";
import { buttonStyle, cardStyle, muted, primaryButtonStyle, chipStyle, inputStyle, latestAssistantPayload, useElapsed } from "./AdminIntelligenceShared";

// ─── Formatting helpers ───────────────────────────────────────────────────────

function fmtDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtNum(value) {
  if (value == null || value === "") return "-";
  const num = Number(value);
  return Number.isFinite(num) ? num.toLocaleString("en-IN") : String(value);
}

function fmtPct(value) {
  const num = Number(value);
  return Number.isFinite(num) ? `${(num * 100).toFixed(1)}%` : "-";
}

function cellValue(key, value) {
  if (value == null || value === "") return "-";
  if (/endts|startdate|enddate|firstseen|lastseen/i.test(key)) return fmtDate(value);
  if (/rate$/i.test(key)) return fmtPct(value);
  if (typeof value === "number") return fmtNum(value);
  return String(value);
}

// ─── Column label map ─────────────────────────────────────────────────────────

const COL_LABEL = {
  BidNumber: "Bid Number",
  EndTS: "End Date",
  Ministry: "Ministry",
  Department: "Department",
  CategoryTag: "Category",
  BidType: "Bid Type",
  Items: "Items",
  L1SellerName: "L1 Seller",
  L1TotalPrice: "L1 Total Price",
  UnitPrice: "Unit Price",
  total_bidders: "Total Bidders",
  qualified_bidders: "Qualified Bidders",
  win_count: "Wins",
  avg_l1_price: "Avg L1 Price",
  min_l1_price: "Min L1 Price",
  max_l1_price: "Max L1 Price",
  median_l1_price: "Median L1 Price",
  participant_min_unit_price: "Min Unit Price",
  participant_max_unit_price: "Max Unit Price",
  participant_median_unit_price: "Median Unit Price",
  linked_contracts: "Linked Contracts",
  linked_total_contract_value: "Total Contract Value",
  avg_linked_contract_value: "Avg Contract Value",
  bids: "Bids",
  avg_bidders_per_bid: "Avg Bidders",
  qualification_rate: "Qualification Rate",
  single_bidder_rate: "Single Bidder Rate",
  seller_clean: "Seller",
  participation_count: "Bids",
  win_rate_pct: "Win %",
  avg_rank: "Avg Rank",
  avg_bid_price: "Avg Bid Price",
  seller_state: "State",
  likelihood: "Likelihood",
  buyer_state: "Buyer State",
  match_dimension: "Match Type",
  items_text: "Items/Description",
  brand: "Brand",
  product_name: "Product",
  total_bids: "Total Bids",
  wins: "Wins",
  avg_unit_price: "Avg Unit Price",
  min_unit_price: "Min Unit Price",
  total_contracts: "Contracts",
};

// ─── Report section sub-components ───────────────────────────────────────────

const SECTION_TH = { textAlign: "left", padding: "8px 10px", borderBottom: "1px solid #e5e7eb", background: "#f8fafc", fontSize: 12, color: "#475569", whiteSpace: "nowrap", fontWeight: 700 };
const SECTION_TD = { padding: "8px 10px", borderBottom: "1px solid #f1f5f9", verticalAlign: "top", fontSize: 13, wordBreak: "break-word" };

// Thin table that never forces horizontal scroll — only shows named columns.
function ReportTable({ rows, columns }) {
  if (!rows?.length) return <div style={{ ...muted, padding: 8 }}>No data.</div>;
  return (
    <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 14 }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {columns.map(([key, lbl]) => (
              <th key={key} style={SECTION_TH}>{lbl}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx}>
              {columns.map(([key]) => (
                <td key={key} style={SECTION_TD}>{cellValue(key, row[key])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Section 1: clean two-column key-value grid.
const BID_FIELDS = [
  ["BidNumber", "Bid Number"],
  ["Ministry", "Ministry"],
  ["Department", "Department"],
  ["State", "State"],
  ["District", "District"],
  ["BidType", "Bid Type"],
  ["CategoryTag", "Category"],
  ["Items", "Items"],
  ["StartDate", "Start Date"],
  ["EndDate", "End Date"],
  ["BidDuration", "Duration"],
  ["BidDocumentNo", "Document No"],
  ["BidStatus", "Status"],
];

function BidOverviewSection({ bid }) {
  if (!bid) return <div style={muted}>No bid data.</div>;
  const visible = BID_FIELDS.filter(([key]) => bid[key] != null && bid[key] !== "");
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(120px, 180px) 1fr", gap: "8px 16px", alignItems: "start" }}>
      {visible.map(([key, lbl]) => (
        <React.Fragment key={key}>
          <div style={{ fontWeight: 700, color: "#475569", fontSize: 13 }}>{lbl}</div>
          <div style={{ fontSize: 13, lineHeight: 1.5 }}>
            {/date|start|end/i.test(lbl) ? fmtDate(bid[key]) : String(bid[key])}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

// Section 4: stat cards — one card per price metric, never overflows.
const PRICE_STATS = [
  ["min_l1_price", "Min L1 Price"],
  ["max_l1_price", "Max L1 Price"],
  ["median_l1_price", "Median L1 Price"],
  ["participant_min_unit_price", "Min Unit Price"],
  ["participant_max_unit_price", "Max Unit Price"],
  ["participant_median_unit_price", "Median Unit Price"],
  ["linked_contracts", "Linked Contracts"],
  ["linked_total_contract_value", "Total Contract Value"],
  ["avg_linked_contract_value", "Avg Contract Value"],
];

function PriceStatCards({ row }) {
  if (!row) return <div style={muted}>No price data.</div>;
  const visible = PRICE_STATS.filter(([key]) => row[key] != null && row[key] !== "");
  if (!visible.length) return <div style={muted}>No price data.</div>;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
      {visible.map(([key, lbl]) => (
        <div key={key} style={{ flex: "1 1 170px", border: "1px solid #e5e7eb", borderRadius: 12, padding: "12px 16px", background: "#f8fafc" }}>
          <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{lbl}</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: "#0f172a" }}>
            {/contracts$/i.test(lbl) ? fmtNum(row[key]) : `₹${fmtNum(row[key])}`}
          </div>
        </div>
      ))}
    </div>
  );
}

// Columns shown for each section — keeps tables narrow enough to fit.
const COLS_SIMILAR = [
  ["BidNumber", COL_LABEL.BidNumber],
  ["EndTS", COL_LABEL.EndTS],
  ["CategoryTag", COL_LABEL.CategoryTag],
  ["L1SellerName", COL_LABEL.L1SellerName],
  ["L1TotalPrice", COL_LABEL.L1TotalPrice],
  ["total_bidders", COL_LABEL.total_bidders],
  ["buyer_state", COL_LABEL.buyer_state],
];
const COLS_WINNERS = [
  ["L1SellerName", COL_LABEL.L1SellerName],
  ["win_count", COL_LABEL.win_count],
  ["avg_l1_price", COL_LABEL.avg_l1_price],
  ["min_l1_price", COL_LABEL.min_l1_price],
  ["max_l1_price", COL_LABEL.max_l1_price],
];
const COLS_COMPETITION = [
  ["BidType", COL_LABEL.BidType],
  ["bids", COL_LABEL.bids],
  ["avg_bidders_per_bid", COL_LABEL.avg_bidders_per_bid],
  ["qualification_rate", COL_LABEL.qualification_rate],
  ["single_bidder_rate", COL_LABEL.single_bidder_rate],
];
const COLS_PARTICIPANTS = [
  ["seller_clean", COL_LABEL.seller_clean],
  ["seller_state", COL_LABEL.seller_state],
  ["participation_count", COL_LABEL.participation_count],
  ["win_count", COL_LABEL.win_count],
  ["win_rate_pct", COL_LABEL.win_rate_pct],
  ["avg_bid_price", COL_LABEL.avg_bid_price],
  ["likelihood", COL_LABEL.likelihood],
];
const COLS_LOCAL_SELLERS = [
  ["seller_clean", COL_LABEL.seller_clean],
  ["seller_state", COL_LABEL.seller_state],
  ["total_bids", COL_LABEL.total_bids],
  ["wins", COL_LABEL.wins],
  ["win_rate_pct", COL_LABEL.win_rate_pct],
  ["avg_bid_price", COL_LABEL.avg_bid_price],
];
const COLS_CHEAPEST = [
  ["seller_clean", COL_LABEL.seller_clean],
  ["seller_state", COL_LABEL.seller_state],
  ["avg_unit_price", COL_LABEL.avg_unit_price],
  ["min_unit_price", COL_LABEL.min_unit_price],
  ["total_contracts", COL_LABEL.total_contracts],
];
const COLS_BIDDER_POOL = [
  ["seller_clean", COL_LABEL.seller_clean],
  ["seller_state", COL_LABEL.seller_state],
  ["participation_count", COL_LABEL.participation_count],
  ["win_count", COL_LABEL.win_count],
  ["win_rate_pct", COL_LABEL.win_rate_pct],
  ["avg_rank", COL_LABEL.avg_rank],
  ["avg_bid_price", COL_LABEL.avg_bid_price],
];

// ── Buyer Behavior card ──────────────────────────────────────────────────────

function BuyerBehaviorCard({ data }) {
  if (!data) return <div style={muted}>No buyer behavior data.</div>;
  const strictColor = data.strictness === "strict" ? "#b91c1c" : data.strictness === "moderate" ? "#92400e" : "#15803d";
  return (
    <div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <div style={{ flex: "1 1 170px", border: "1px solid #e5e7eb", borderRadius: 12, padding: "12px 16px", background: "#f8fafc" }}>
          <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Strictness</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: strictColor, textTransform: "uppercase" }}>{data.strictness || "-"}</div>
        </div>
        <div style={{ flex: "1 1 170px", border: "1px solid #e5e7eb", borderRadius: 12, padding: "12px 16px", background: "#f8fafc" }}>
          <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Qualification Rate</div>
          <div style={{ fontSize: 17, fontWeight: 800 }}>{data.avg_qualification_rate != null ? `${data.avg_qualification_rate}%` : "-"}</div>
        </div>
        <div style={{ flex: "1 1 170px", border: "1px solid #e5e7eb", borderRadius: 12, padding: "12px 16px", background: "#f8fafc" }}>
          <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Disqualification Rate</div>
          <div style={{ fontSize: 17, fontWeight: 800 }}>{data.avg_disqualification_rate != null ? `${data.avg_disqualification_rate}%` : "-"}</div>
        </div>
        <div style={{ flex: "1 1 170px", border: "1px solid #e5e7eb", borderRadius: 12, padding: "12px 16px", background: "#f8fafc" }}>
          <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Single Bidder Rate</div>
          <div style={{ fontSize: 17, fontWeight: 800 }}>{data.single_bidder_rate != null ? `${data.single_bidder_rate}%` : "-"}</div>
        </div>
        <div style={{ flex: "1 1 170px", border: "1px solid #e5e7eb", borderRadius: 12, padding: "12px 16px", background: "#f8fafc" }}>
          <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Sample Bids</div>
          <div style={{ fontSize: 17, fontWeight: 800 }}>{fmtNum(data.sample_bids)}</div>
        </div>
      </div>
      {data.warnings?.length ? (
        <div style={{ background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: 10, padding: "10px 14px" }}>
          {data.warnings.map((w, i) => <div key={i} style={{ fontSize: 13, lineHeight: 1.6, color: "#92400e", fontWeight: 600 }}>{w}</div>)}
        </div>
      ) : null}
    </div>
  );
}

// ── Price Prediction card ────────────────────────────────────────────────────

function PricePredictionCard({ data }) {
  if (!data || !Object.keys(data).length) return <div style={muted}>No price prediction data.</div>;
  const benchmarks = [data.local_benchmark, data.national_benchmark].filter(Boolean);
  return (
    <div>
      {benchmarks.map((b, idx) => (
        <div key={idx} style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: "#334155" }}>
            {idx === 0 && data.local_benchmark ? `Local Benchmark (${b.buyer_state || b.category_tag || "?"})` : "National Benchmark"}
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {[["avg_l1_price", "Avg L1"], ["min_l1_price", "Min L1"], ["max_l1_price", "Max L1"], ["p25_unit_price", "P25 Unit"], ["avg_unit_price", "Avg Unit"], ["p75_unit_price", "P75 Unit"]].map(([k, lbl]) =>
              b[k] != null ? (
                <div key={k} style={{ flex: "1 1 130px", border: "1px solid #e5e7eb", borderRadius: 10, padding: "8px 12px", background: "#f8fafc" }}>
                  <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>{lbl}</div>
                  <div style={{ fontSize: 15, fontWeight: 800 }}>{`₹${fmtNum(b[k])}`}</div>
                </div>
              ) : null
            )}
          </div>
        </div>
      ))}
      {data.suggested_range ? (
        <div style={{ background: "#dcfce7", border: "1px solid #22c55e", borderRadius: 10, padding: "10px 14px", marginTop: 8 }}>
          <span style={{ fontWeight: 700, color: "#15803d" }}>Suggested Range: </span>
          <span style={{ fontWeight: 800, color: "#15803d" }}>{`₹${fmtNum(data.suggested_range.low)} – ₹${fmtNum(data.suggested_range.high)}`}</span>
          <span style={{ color: "#15803d", marginLeft: 8 }}>({data.suggested_range.basis})</span>
        </div>
      ) : null}
      {data.direct_order_comparison ? (
        <div style={{ marginTop: 10, fontSize: 13, color: "#475569", lineHeight: 1.6 }}>
          <strong>Direct vs Bid:</strong> Direct orders avg ₹{fmtNum(data.direct_order_comparison.direct_avg_unit_price)} ({data.direct_order_comparison.direct_count || 0} orders) vs Bid avg ₹{fmtNum(data.direct_order_comparison.bid_avg_unit_price)} ({data.direct_order_comparison.bid_count || 0} bids)
          {data.direct_order_comparison.price_gap_pct != null ? ` — gap: ${Number(data.direct_order_comparison.price_gap_pct).toFixed(1)}%` : ""}
        </div>
      ) : null}
    </div>
  );
}

// ─── Main tab component ───────────────────────────────────────────────────────
// Architecture: same POST + poll pattern as Tab 1.
//
// POST /live-bid-report creates the job and returns immediately. The server
// processes all stages in the background (fire-and-forget). The client polls
// GET /sessions/:id?advance=false every 3s, switching to advance=step after
// ~18s as fallback in case the background job stalled. Hard cap at ~150s.

export default function AdminLiveBidIntelligenceTab({ sessionId, session, messages: propMessages, sessionLoading, onSessionStarted, onSessionResolved, onNewSession }) {
  const [bidId, setBidId] = useState("");
  const [sourceSystem, setSourceSystem] = useState("gem");
  const [submitting, setSubmitting] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState("");
  const [localReport, setLocalReport] = useState(null);
  const ownGenerateRef = useRef(false);
  const activeGenerateRef = useRef(null);
  const scrollRef = useRef(null);
  const elapsed = useElapsed(submitting);

  // Derive report from propMessages when available for this session.
  const messagesReady = Boolean(sessionId && session?.id === sessionId);
  const propReport = messagesReady ? (latestAssistantPayload(propMessages).raw?.report || null) : null;
  const propError = messagesReady && session?.status === "error" ? (session.error_message || "Report generation failed.") : null;

  // Sync from parent when the user navigates to a past session via the sidebar.
  // Skipped while we are actively generating to prevent wiping in-flight state.
  useEffect(() => {
    if (ownGenerateRef.current) {
      ownGenerateRef.current = false;
      return;
    }
    activeGenerateRef.current = null; // cancel any running poll
    setLocalReport(null);
    setError("");
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cancel polling on unmount (tab switch / resetNonce change).
  useEffect(() => { return () => { activeGenerateRef.current = null; }; }, []);

  // The report to display: localReport (from polling) takes priority, then propReport (from sidebar navigation).
  const displayReport = localReport || propReport;
  const displayError = error || (!submitting && !localReport ? propError : null);

  // Show loading overlay only for sidebar navigation, not during active generation.
  const showLoadingOverlay = (sessionLoading || (sessionId && !messagesReady)) && !submitting && !localReport;

  // Scroll to top when a new report is displayed.
  useEffect(() => {
    if (displayReport && scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [displayReport]);

  async function generate() {
    const clean = String(bidId || "").trim();
    if (!clean || submitting) return;
    setSubmitting(true);
    setError("");
    setLocalReport(null);

    let resolvedSessionId = sessionId || null;
    try {
      // 1. POST creates the job — server returns immediately.
      const postRes = await adminPostJson("/api/admin/intelligence/live-bid-report", {
        session_id: sessionId || null,
        bid_id: clean,
        source_system: sourceSystem,
      });
      resolvedSessionId = postRes.session_id;

      // 2. Tell parent about the new/reused session.
      if (resolvedSessionId !== sessionId) {
        ownGenerateRef.current = true;
        onSessionStarted("tab2", resolvedSessionId);
      }

      // 3. Poll GET every 3s. Start with advance=false (background job handles it).
      //    After ~18s, switch to advance=step as fallback in case background stalled.
      //    Hard cap at ~150s.
      activeGenerateRef.current = resolvedSessionId;
      let done = false;
      let pollCount = 0;
      const FALLBACK_POLLS = 6; // 6 × 3s = 18s
      const MAX_POLLS = 50;     // 50 × 3s = 150s
      while (!done) {
        await new Promise((r) => setTimeout(r, 3000));
        if (activeGenerateRef.current !== resolvedSessionId) return;
        pollCount++;
        if (pollCount > MAX_POLLS) {
          setError("Report generation timed out. Please try again.");
          onSessionResolved("tab2", resolvedSessionId);
          break;
        }
        try {
          const advance = pollCount > FALLBACK_POLLS ? "step" : "false";
          const res = await adminGetJson(
            `/api/admin/intelligence/sessions/${encodeURIComponent(resolvedSessionId)}?advance=${advance}`
          );
          if (activeGenerateRef.current !== resolvedSessionId) return;

          if (res.session?.status === "complete") {
            done = true;
            const payload = latestAssistantPayload(res.messages || []);
            setLocalReport(payload.raw?.report || null);
            onSessionResolved("tab2", resolvedSessionId);
          } else if (res.session?.status === "error") {
            done = true;
            setError(res.session.error_message || "Report generation failed. Please try again.");
            onSessionResolved("tab2", resolvedSessionId);
          }
        } catch (_pollErr) {
          // Network glitch — retry next interval.
        }
      }
    } catch (err) {
      setError(err.message || String(err));
      if (resolvedSessionId) onSessionResolved("tab2", resolvedSessionId);
    } finally {
      if (activeGenerateRef.current === resolvedSessionId) activeGenerateRef.current = null;
      setSubmitting(false);
    }
  }

  async function exportPdf() {
    if (!sessionId || pdfLoading) return;
    try {
      setPdfLoading(true);
      setError("");
      const res = await adminPostJson("/api/admin/intelligence/live-bid-report/export", { session_id: sessionId });
      if (res.export?.signed_url) window.open(res.export.signed_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setPdfLoading(false);
    }
  }

  const rec = displayReport?.strategic_summary?.recommendation;
  const recColor = rec === "GO" ? "#15803d" : rec === "AVOID" ? "#b91c1c" : "#92400e";
  const isEmpty = !displayReport && !submitting && !displayError;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", position: "relative" }}>

      {/* ── Sidebar-navigation loading overlay ── */}
      {showLoadingOverlay ? (
        <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.82)", zIndex: 10, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
          <AdminSpinner loading />
          <div style={{ fontWeight: 700, color: "#334155" }}>Loading report…</div>
        </div>
      ) : null}

      {/* ── Scrollable content area ── */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "8px 8px 0 8px" }}>
        <div style={{ display: "grid", gap: 14, paddingBottom: 8, maxWidth: 960 }}>

          {/* Welcome / empty state */}
          {isEmpty ? (
            <div style={cardStyle}>
              <div style={{ fontSize: 28, fontWeight: 900, marginBottom: 10 }}>Live Bid Intelligence</div>
              <div style={{ ...muted, fontSize: 15, lineHeight: 1.7, maxWidth: 760 }}>
                Enter a GeM bid number or CPPP Tender ID below to generate a full intelligence report — predicted participants, price benchmarks, buyer behavior analysis, location intelligence, bidder pool, competition patterns, and an AI strategic summary.
              </div>
            </div>
          ) : null}

          {/* Generating indicator */}
          {submitting ? (
            <div style={{ ...cardStyle, padding: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 700 }}>
                <AdminSpinner loading />
                <span>Generating report…</span>
              </div>
              <div style={{ ...muted, marginTop: 6 }}>Analysing bid data… {elapsed}s</div>
            </div>
          ) : null}

          {/* Error display */}
          {displayError ? (
            <div style={{ ...cardStyle, color: "#b91c1c", fontWeight: 700 }}>Error: {displayError}</div>
          ) : null}

          {/* ── Report sections ── */}
          {displayReport ? (
            <>
              <div style={cardStyle}>
                <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 14 }}>Bid Overview</div>
                <BidOverviewSection bid={displayReport.bid_overview} />
              </div>

              <div style={cardStyle}>
                <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>Historical Similar Bids</div>
                <div style={{ ...muted, marginBottom: 10 }}>
                  {(displayReport.historical_similar_bids || []).length} similar closed bids found
                </div>
                <ReportTable rows={displayReport.historical_similar_bids || []} columns={COLS_SIMILAR} />
              </div>

              <div style={cardStyle}>
                <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>Who Typically Wins</div>
                <ReportTable rows={displayReport.who_typically_wins || []} columns={COLS_WINNERS} />
              </div>

              <div style={cardStyle}>
                <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 14 }}>Price Intelligence</div>
                <PriceStatCards row={displayReport.price_intelligence} />
              </div>

              <div style={cardStyle}>
                <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>Competition Patterns</div>
                <ReportTable rows={displayReport.competition_patterns || []} columns={COLS_COMPETITION} />
              </div>

              {/* ── Predicted Participants ── */}
              {displayReport.predicted_participants?.length ? (
                <div style={cardStyle}>
                  <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>Predicted Participants</div>
                  <div style={{ ...muted, marginBottom: 10 }}>
                    Sellers most likely to bid, based on past category &amp; location patterns
                  </div>
                  <ReportTable rows={displayReport.predicted_participants} columns={COLS_PARTICIPANTS} />
                </div>
              ) : null}

              {/* ── Price Prediction ── */}
              {displayReport.price_prediction ? (
                <div style={cardStyle}>
                  <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>Price Prediction</div>
                  <PricePredictionCard data={displayReport.price_prediction} />
                </div>
              ) : null}

              {/* ── Buyer Behavior ── */}
              {displayReport.buyer_behavior ? (
                <div style={cardStyle}>
                  <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>Buyer Behavior</div>
                  <BuyerBehaviorCard data={displayReport.buyer_behavior} />
                </div>
              ) : null}

              {/* ── Location Intelligence ── */}
              {(displayReport.location_intelligence?.local_sellers?.length || displayReport.location_intelligence?.national_cheapest?.length) ? (
                <div style={cardStyle}>
                  <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>Location Intelligence</div>
                  {displayReport.location_intelligence.local_sellers?.length ? (
                    <>
                      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: "#334155" }}>Local Sellers (same state)</div>
                      <ReportTable rows={displayReport.location_intelligence.local_sellers} columns={COLS_LOCAL_SELLERS} />
                    </>
                  ) : null}
                  {displayReport.location_intelligence.national_cheapest?.length ? (
                    <>
                      <div style={{ fontWeight: 700, fontSize: 13, marginTop: 16, marginBottom: 8, color: "#334155" }}>National Cheapest Sellers</div>
                      <ReportTable rows={displayReport.location_intelligence.national_cheapest} columns={COLS_CHEAPEST} />
                    </>
                  ) : null}
                </div>
              ) : null}

              {/* ── Bidder Pool ── */}
              {displayReport.bidder_pool?.length ? (
                <div style={cardStyle}>
                  <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>Bidder Pool Analysis</div>
                  <div style={{ ...muted, marginBottom: 10 }}>
                    Top active bidders for this category with historical performance
                  </div>
                  <ReportTable rows={displayReport.bidder_pool} columns={COLS_BIDDER_POOL} />
                </div>
              ) : null}

              <div style={cardStyle}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>Gemini Strategic Summary</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    {rec ? (
                      <span style={{ ...chipStyle, background: rec === "GO" ? "#dcfce7" : rec === "AVOID" ? "#fee2e2" : "#fef3c7", color: recColor, borderColor: recColor, fontSize: 14, fontWeight: 900 }}>
                        {rec}
                      </span>
                    ) : null}
                    {displayReport.strategic_summary?.suggested_price_range ? (
                      <span style={chipStyle}>{displayReport.strategic_summary.suggested_price_range}</span>
                    ) : null}
                  </div>
                </div>
                <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.7, marginBottom: 12 }}>
                  {displayReport.strategic_summary?.executive_summary || "No summary available."}
                </div>
                {(displayReport.strategic_summary?.key_points || []).length ? (
                  <ul style={{ margin: "0 0 0 18px", padding: 0 }}>
                    {displayReport.strategic_summary.key_points.map((item, idx) => (
                      <li key={idx} style={{ marginBottom: 6, lineHeight: 1.6 }}>{item}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* ── Input bar — always pinned at bottom ── */}
      <div style={{ borderTop: "1px solid #e5e7eb", background: "#fff", padding: "6px 10px", flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select
            style={{ ...inputStyle, width: 90, minHeight: 42, flexShrink: 0, fontSize: 13, fontWeight: 700, cursor: "pointer" }}
            value={sourceSystem}
            onChange={(e) => setSourceSystem(e.target.value)}
          >
            <option value="gem">GeM</option>
            <option value="cppp">CPPP</option>
          </select>
          <input
            style={{ ...inputStyle, flex: 1, minHeight: 42 }}
            placeholder={sourceSystem === "cppp" ? "e.g. CPPP Tender ID" : "e.g. GEM/2025/B/1234567"}
            value={bidId}
            onChange={(e) => setBidId(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") generate(); }}
          />
          <button
            style={{ ...primaryButtonStyle, minWidth: 80, height: 42, flexShrink: 0, fontSize: 13 }}
            onClick={generate}
            disabled={submitting || !String(bidId || "").trim()}
          >
            {submitting ? "Generating…" : "Generate"}
          </button>
          {displayReport ? (
            <button
              style={{ ...buttonStyle, height: 42, flexShrink: 0, fontSize: 13, display: "flex", alignItems: "center", gap: 6, opacity: pdfLoading ? 0.7 : 1 }}
              onClick={exportPdf}
              disabled={pdfLoading}
            >
              {pdfLoading ? "PDF…" : "PDF"}
            </button>
          ) : null}
          <button style={{ border: 0, background: "transparent", padding: 0, color: "#2563eb", cursor: "pointer", fontWeight: 700, fontSize: 12, flexShrink: 0, whiteSpace: "nowrap" }} onClick={onNewSession}>+ New</button>
        </div>
      </div>
    </div>
  );
}
